'use client';

import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { Suspense, useEffect, useRef } from 'react';

const IS_DEV = process.env.NODE_ENV === 'development';
const CID = process.env.NEXT_PUBLIC_FIRSTPROMOTER_CID;
const FORCE_DEV = process.env.NEXT_PUBLIC_FIRSTPROMOTER_FORCE === '1';

declare global {
  interface Window {
    fpr?: (...args: unknown[]) => void;
  }
}

// No `useSearchParams()` here: under cacheComponents the hook turns this
// boundary into a permanent dynamic hole in every static shell, and an aborted
// client-side resume throws recoverable React #419 for it. The click ping is
// funnel-event granularity (attribution cookie is set on the LP), so firing per
// pathname change only — not per query change — loses nothing that matters.
function FirstPromoterClickTracker() {
  const pathname = usePathname();
  const initial = useRef(true);

  useEffect(() => {
    if (initial.current) {
      initial.current = false;
      return;
    }
    window.fpr?.('click');
  }, [pathname]);

  return null;
}

export function FirstPromoterProvider() {
  if (!CID) return null;
  if (IS_DEV && !FORCE_DEV) return null;

  // Two-script init: the inline script defines the fpr() queue and points the SDK
  // at our same-origin proxy (/fptr/*). The proxy variant of fpr.js drains the
  // queue on load. Same-origin tracking is what makes _fprom_tid land on
  // .taboo.tv as a first-party cookie — direct calls to t.firstpromoter.com get
  // blocked or partitioned by modern browsers.
  return (
    <>
      <Script
        id="fp-init"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w){
              w.fpr=w.fpr||function(){
                w.fpr.q = w.fpr.q||[];
                w.fpr.q[arguments[0]=='set'?'unshift':'push'](arguments);
              };
              w.proxy_fpr_tr_url="/fptr/tr";
              w.proxy_fpr_get_details_url="/fptr/get_details";
            })(window);
            fpr("init", {cid:"${CID}"});
            fpr("click");
          `,
        }}
      />
      <Script id="fp-sdk" src="/fpr-prox.js" strategy="lazyOnload" defer />
      <Suspense fallback={null}>
        <FirstPromoterClickTracker />
      </Suspense>
    </>
  );
}
